import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DailyReport } from '../objects/daily-report';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class DailyReportService {
  private apiUrl = environment.apiUrl + 'DailyReports';

  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  private reportsSubject = new BehaviorSubject<DailyReport[]>([]);
  public reports$ = this.reportsSubject.asObservable();

  constructor(private http: HttpClient) {}

  getByProject(projectId: number): Observable<DailyReport[]> {
    return this.http
      .get<DailyReport[]>(`${this.apiUrl}/project/${projectId}`)
      .pipe(
        tap(r => this.reportsSubject.next(r)),
        catchError(() => this.getMockData(projectId))
      );
  }

  create(report: Partial<DailyReport>): Observable<DailyReport> {
    return this.http.post<DailyReport>(this.apiUrl, report, this.httpOptions).pipe(
      tap(r => this.reportsSubject.next([...this.reportsSubject.value, r])),
      catchError(err => {
        console.error('DailyReport create failed, saving locally', err);
        const local: DailyReport = { ...report, id: Date.now() } as DailyReport;
        this.reportsSubject.next([...this.reportsSubject.value, local]);
        return of(local);
      })
    );
  }

  update(id: number, report: DailyReport): Observable<DailyReport> {
    return this.http.put<DailyReport>(`${this.apiUrl}/${id}`, report, this.httpOptions).pipe(
      tap(updated => {
        const current = this.reportsSubject.value.map(r => r.id === id ? updated : r);
        this.reportsSubject.next(current);
      }),
      catchError(err => {
        console.error('DailyReport update failed, updating locally', err);
        const current = this.reportsSubject.value.map(r => r.id === id ? report : r);
        this.reportsSubject.next(current);
        return of(report);
      })
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, this.httpOptions).pipe(
      tap(() => this.reportsSubject.next(this.reportsSubject.value.filter(r => r.id !== id))),
      catchError(err => {
        console.error('DailyReport delete failed, removing locally', err);
        this.reportsSubject.next(this.reportsSubject.value.filter(r => r.id !== id));
        return of(null);
      })
    );
  }

  getMockData(projectId: number): Observable<DailyReport[]> {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const mock: DailyReport[] = [
      {
        id: 1,
        projectId,
        reportDate: yesterday.toISOString().split('T')[0],
        foremanName: 'Mike Johnson',
        weatherConditions: 'Clear',
        temperature: '72°F',
        workersOnSite: 8,
        hoursWorked: 8,
        workCompleted: 'Completed framing of east wall. Began roof truss installation.',
        materialsUsed: 'Lumber (2x6, 200 pcs), Nails (50 lbs), Hurricane straps (40)',
        issuesEncountered: 'Minor delay due to delivery truck arriving 2 hours late.',
        safetyIncidents: 'None',
        requiresInspection: false,
        inspectionDetails: '',
        delaysOrSetbacks: '2 hour material delay',
        nextDayPlan: 'Complete roof truss installation. Start sheathing.',
        createdAt: yesterday.toISOString()
      },
      {
        id: 2,
        projectId,
        reportDate: today.toISOString().split('T')[0],
        foremanName: 'Mike Johnson',
        weatherConditions: 'Cloudy',
        temperature: '65°F',
        workersOnSite: 10,
        hoursWorked: 8,
        workCompleted: 'Finished roof truss installation. 60% of sheathing complete.',
        materialsUsed: 'OSB Sheathing (40 sheets), Roofing nails (20 lbs)',
        issuesEncountered: 'None',
        safetyIncidents: 'None',
        requiresInspection: true,
        inspectionDetails: 'Framing inspection needed before insulation can start.',
        delaysOrSetbacks: '',
        nextDayPlan: 'Complete sheathing. Schedule framing inspection.',
        createdAt: today.toISOString()
      }
    ];
    this.reportsSubject.next(mock);
    return of(mock);
  }
}
